import $ from 'jquery';
import Numeral from 'numeral';

import { OLD_FIREFOX_VERSION } from './constants.es6';

/**
 * Path separator for diving into objects.
 *
 * @type {RegExp}
 */
export const PATH_SEPARATOR = /\./;

/**
 * Numeral configuration
 */
Numeral.register('locale', 'fr', {
    delimiters: {
        thousands: ' ',
        decimal: ',',
    },
    abbreviations: {
        thousand: 'k',
        million: 'm',
        billion: 'b',
        trillion: 't',
    },
    ordinal(number) {
        return number === 1 ? 'er' : 'ème';
    },
    currency: {
        symbol: '€',
    },
});
Numeral.locale('fr');

/**
 * Return a string in a currency format (Euros)
 *
 * @param {Number} value
 */
export function formatCurrency(value) {
    return Numeral(value).format('0,0[.]0 $');
}

/**
 *
 *
 * @returns {Object}
 */
export function getURLParams() {
    const search = window.location.search.substring(1);

    if (search === '') {
        return {};
    }

    return decodeURI(search)
        .split('&')
        .reduce((params, currentParam) => {
            const [key, value] = currentParam.split('=');

            if (key.endsWith('[]')) {
                const rawKey = key.slice(0, -2);
                return {
                    ...params,
                    [rawKey]: Array.isArray(params[rawKey]) ? [...params[rawKey], value] : [value],
                };
            }

            return { ...params, [key]: value };
        }, {});
}

/**
 * Return a value from a given key of the URL
 *
 * @param {String} name Key name of the parameter
 * @param {String} defaultValue
 * @returns {String}
 */
export function getURLParam(name, defaultValue) {
    let value;
    try {
        value = getURLParams()[name];
    } catch (e) {
        value = defaultValue;
    }
    return value;
}

/**
 * Parse all form values and build an URL with
 * @param {HTMLFormElement} form
 * @param {Object} extraParams
 * @return {string} url
 */
export function formDataToURL(form, extraParams) {
    const formData = $(form).serializeArray();
    const data = {};

    Object.keys(formData).forEach(key => {
        const nameFormData = formData[key].name;
        const valueFormData = formData[key].value;
        if (nameFormData.charAt(0) !== '_') {
            // Unwanted params values to clean URL
            if (nameFormData in data) {
                data[nameFormData] = `${data[nameFormData]},${valueFormData}`;
            } else {
                data[nameFormData] = valueFormData;
            }
        }
    });

    if (extraParams) {
        Object.assign(data, extraParams);
    }
    if (String(data.page) === '1') {
        delete data.page; // Needed for SEO purpose
    }

    const queryString = $.param(data);
    const url = `${form.getAttribute('action')}?${queryString}`;
    return url;
}

/**
 * add params to a string url testing first if it contains already a param
 * @param {String} src string url to which add params
 * @param {Object} obj params to add
 * @returns {String}
 */
export function addURLParams(src, obj) {
    const params = $.param(obj);
    if (!params) {
        return src;
    }

    return src + (src.indexOf('?') !== -1 ? '&' : '?') + params;
}

/**
 * Merge objects. Alternative to jQuery.extend
 * Usage : mergeObjects({}, objA, objB);
 *
 * @param {*} out Objects
 * @returns {Object}
 */
export function mergeObjects(out = {}, ...objects) {
    return objects.reduce(
        (acc, current = {}) => ({
            ...acc,
            ...current,
        }),
        out,
    );
}

/**
 * Test two Set equality
 *
 * @param {Set} set1
 * @param {Set} set2
 * @return {boolean} is sets are equals or not
 */
export function setEquals(set1, set2) {
    if (set1.size !== set2.size) {
        return false;
    }
    let equalElements = 0;
    set1.forEach(value => {
        if (set2.has(value)) {
            equalElements += 1;
        }
    });
    if (set1.size !== equalElements) {
        return false;
    }
    return true;
}

/**
 * Wrapper for browser console
 * @exemple
 * Utils.logger(myVariable1, myVariable2); // console.log(myVariable1, myVariable2)
 * Utils.logger('warn', myVariable1, myVariable2); // console.warn(myVariable1, myVariable2)
 * Utils.logger('info', myVariable1, myVariable2); // console.info(myVariable1, myVariable2)
 *
 * @param {...number} items Items to log
 */
export function logger(...args) {
    let type = args[0];

    let items;
    if (!window.console) {
        return;
    }
    items = Array.prototype.slice.call(args, 1);
    if (typeof window.console[type] !== 'function') {
        type = 'log';
        items = args;
    }
    if (MA.Config && MA.Config.DEBUG) {
        window.console[type](...items);
    }
}

let _browser = null;

/**
 * Get browser name and version
 *
 * @return {*}
 */
export function getBrowser() {
    if (_browser) {
        return _browser;
    }
    const ua = navigator.userAgent;
    let uaMatches = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
    let tem;
    if (/trident/i.test(uaMatches[1])) {
        tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
        _browser = {
            browserName: 'IE',
            browserVersion: parseFloat(tem[1] || ''),
        };
        return _browser;
    }
    if (/chrome/i.test(uaMatches[1])) {
        tem = ua.match(/\b(OPR|Edge)\/(\d+)/);
        if (tem !== null) {
            _browser = {
                browserName: tem.slice(1)[0].replace('OPR', 'Opera'),
                browserVersion: parseFloat(tem.slice(1)[1]),
            };
            return _browser;
        }
    }
    uaMatches = uaMatches[2]
        ? [uaMatches[1], uaMatches[2]]
        : [navigator.appName, navigator.appVersion, '-?'];
    tem = ua.match(/version\/(\d+)/i);
    if (tem !== null) {
        uaMatches.splice(1, 1, tem[1]);
    }
    _browser = {
        browserName: uaMatches[0],
        browserVersion: parseFloat(uaMatches[1]),
    };
    return _browser;
}

/**
 *  @return {Boolean}
 */
export function isOldFirefox() {
    const browser = getBrowser();
    return browser.browserName === 'Firefox' && browser.browserVersion < OLD_FIREFOX_VERSION;
}

/**
 *
 * @return {Boolean}
 */
export function isIE() {
    return getBrowser().browserName === 'IE';
}

/**
 * Check if two items are deeply equals.
 *
 * @param  {*} o1 First item
 * @param  {*} o2 Second item
 * @return {bool} True is they are deeply equal, false otherwise
 */
export function deepEqual(o1, o2) {
    if (o1 === o2) {
        return true;
    }

    if (typeof o1 !== typeof o2) {
        return false;
    }

    const typeO1 = typeof o1;

    if (typeO1 === 'number' && Number.isNaN(o1) && Number.isNaN(o2)) {
        return true;
    }

    if (
        typeO1 === 'undefined' ||
        typeO1 === 'null' ||
        typeO1 === 'string' ||
        typeO1 === 'number' ||
        typeO1 === 'boolean'
    ) {
        return false;
    }

    if (o1 instanceof Date && o2 instanceof Date) {
        return o1.getTime() === o2.getTime();
    }

    if (Array.isArray(o1)) {
        if (o1.length !== o2.length) {
            return false;
        }

        for (let i = 0, size = o1.length; i < size; i += 1) {
            if (!deepEqual(o1[i], o2[i])) {
                return false;
            }
        }
    }

    return false;
}

/**
 * Helper to crate a function with a fallback SetTimeout
 * User case : handle hitCallback when GA service not responding
 * https://developers.google.com/analytics/devguides/collection/analyticsjs/sending-hits
 *
 * @param {Function} callback
 * @param {Number} optTimeout
 * @return {Function}
 */
export function createFunctionWithTimeout(callback, optTimeout) {
    let called = false;
    function fn() {
        if (!called) {
            called = true;
            callback();
        }
    }

    setTimeout(fn, optTimeout || 1000);
    return fn;
}

/**
 * Check if params has a value.
 *
 * @param {Any}  e Tested value
 * @return {Boolean} True if tested value is not undefined nor null.
 */
export function hasValue(e) {
    return typeof e !== 'undefined' && e !== null;
}

/**
 * Get value from object path.
 *
 * @param  {Object} object  Object explored
 * @param  {String} keyPath Path from object's root
 * @return {Any}            Value at given place, or undefined in not found
 */
export function getValueFromPath(object, keyPath) {
    try {
        return keyPath.split(PATH_SEPARATOR).reduce((o, i) => o[i], object);
    } catch (error) {
        return undefined;
    }
}

/**
 * Determine wether or not given parameter is a function.
 * @param  {Any} fn   A possible function
 * @return {Boolean}  True if function, false otherwise
 */
export function isFunction(fn) {
    return fn instanceof Function;
}
